// Resolves server-hosted asset paths (listing photos, avatars, inspection photos)
// to something the current runtime can actually fetch.
//
// The API returns these as root-relative paths -- `/uploads/listings/{...}.webp`.
// In a browser that is correct and needs no work: the page and the API share an
// origin, so the path resolves against the site itself.
//
// It breaks in the Capacitor mobile app. There the web build is bundled into the
// app and served from `https://localhost`, so `/uploads/...` resolves against the
// WebView's own origin and 404s -- every listing image renders broken. API calls
// already avoid this because they go through VITE_API_URL, which the mobile build
// sets to an absolute host; images had no equivalent until this.
//
// The base is derived from VITE_API_URL rather than a new variable, so there is
// exactly one thing to configure per build and the two can never disagree:
//   - web      VITE_API_URL unset -> defaults to '/api/v1' -> base '' -> unchanged
//   - mobile   VITE_API_URL=https://test.igotthatus.com/api/v1 -> base is that origin
//
// VITE_ASSET_BASE overrides it outright, for the case where uploads later move to
// their own host or a CDN.

function resolveAssetBase(): string {
  const explicit = import.meta.env.VITE_ASSET_BASE
  if (explicit) return String(explicit).replace(/\/+$/, '')

  const apiUrl = import.meta.env.VITE_API_URL
  if (!apiUrl) return '' // browser default: same-origin, keep paths relative

  try {
    // Absolute API URL -> assets live on that same host.
    return new URL(apiUrl).origin
  } catch {
    // Relative API URL (e.g. '/api/v1'), so same-origin applies here too.
    return ''
  }
}

const ASSET_BASE = resolveAssetBase()

/**
 * Returns a URL safe to put in an `<img src>` from any runtime.
 *
 * Passes through anything already resolvable -- absolute URLs, data:/blob: URIs,
 * and bundled assets -- and only rewrites the server-hosted `/uploads/` paths the
 * API produces. Returns undefined for empty input so callers can keep using their
 * existing `{photo ? <img/> : <placeholder/>}` checks unchanged.
 */
export function assetUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined
  if (!ASSET_BASE) return path
  // Already fully qualified (http, https, data:, blob:, //cdn...) -- leave alone.
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(path)) return path
  // Only server-hosted uploads need rebasing. Bundled assets (/icon-192x192.png,
  // /placeholder-tool.svg) ship inside the app and must stay relative.
  if (!path.startsWith('/uploads/')) return path
  return ASSET_BASE + path
}

export default assetUrl
